import { Dialog as Ark } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizes = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

export function Dialog({ open, onOpenChange, title, description, children, className, footer, size = 'md' }: DialogProps) {
  return (
    <Ark.Root open={open} onOpenChange={(d) => onOpenChange(d.open)} lazyMount unmountOnExit>
      <Portal>
        <Ark.Backdrop className="fixed inset-0 z-[1000] bg-stone-950/25 backdrop-blur-[2px] data-[state=open]:animate-fade-in dark:bg-black/50" />
        <Ark.Positioner className="fixed inset-0 z-[1001] flex items-start justify-center overflow-y-auto p-4 pt-[10vh]">
          <Ark.Content
            data-lumo-modal={open ? 'open' : 'closed'}
            className={cn(
              'relative w-full rounded-modal bg-surface shadow-modal outline-none data-[state=open]:animate-pop-in',
              sizes[size],
              className,
            )}
          >
            <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-3">
              <div>
                <Ark.Title className="text-[17px] font-semibold tracking-tight">{title}</Ark.Title>
                {description && <Ark.Description className="mt-1 text-sm text-fg-muted">{description}</Ark.Description>}
              </div>
              <Ark.CloseTrigger
                aria-label={t('common.close')}
                className="-mr-2 inline-flex size-8 items-center justify-center rounded-control text-fg-muted hover:bg-hover hover:text-fg"
              >
                <X className="size-[18px]" />
              </Ark.CloseTrigger>
            </div>
            <div className="px-6 pb-6">{children}</div>
            {footer && <div className="flex justify-end gap-2 border-t border-border px-6 py-4">{footer}</div>}
          </Ark.Content>
        </Ark.Positioner>
      </Portal>
    </Ark.Root>
  );
}
